import * as THREE from 'three';
import type { ColliderHandle, RoomCapabilities, RoomEnvironment, RoomModule, RoomModuleDefinition, RoomTick, ZoneBlend } from '@/game/roomModule';
import type { RoomDiagnostics } from '@/game/roomDiagnostics';
import type { Pose } from '@/anim/types';
import { getAsset } from '@/models/registry';
import { brassBellAsset, createHeldBell } from '@/models/env/brass-bell';
import { endingZone, getZone } from '@/render/palette';
import { getWear } from '@/render/toon';
import { DAWN_ZENITH, HEDGEROW_BLOOM_ZONE_ID, HEDGEROW_FOG_SCALE, createHedgerow, hedgerowLighting, hedgerowSolids } from '@/world/hedgerow';
import { HILL_BELL_AT, HILL_BELL_Y, HILL_KNOLL_SOUTH, HILL_ROAD, HILL_SPAWN, createHillDressing, hillSolids } from '@/world/hill';
import { collapseRigged } from '@/world/instancing';
import { EndingStaging, type EndingStage } from '@/world/hillStaging';
import { ENDING_CUES, ENDING_FLAGS, ENDING_IDS } from '@/story/act3/ending';

/**
 * Beat 14's room: the hedgerow's far field at dawn gold, with a knoll in it
 * and the road in a cutting below (`docs/beat-14-plan.md`, `src/world/hill.ts`).
 *
 * **A room id of its own (`hill`), not a mode of `hedgerow`**, for beat 13's
 * reason and one more: the hedgerow is still wired by branches in `main.ts`
 * (beats 7 and 10), and a third beat's worth of conditionals there is exactly
 * what the pathfinder rule exists to stop. The geometry is the hedgerow's —
 * `createHedgerow({ road: false })` — so this is the same field, not a copy.
 *
 * No stealth, no watcher, no companion, no `F`. One interactable: the bell.
 */

const MAX_FRAME = 0.1;

/** The sky's middle band at the open: the outside zone's key, dimmed toward a horizon glow. */
const DAWN_GOLD = 0xd8b27c;

/**
 * The drain (the bloom in reverse, `docs/act-3-plan.md`): its floor and its
 * ceiling in each stage, and how fast it moves between them. It is a clock
 * inside a band rather than one clock for the beat, because stage 2 is the
 * player's and can take ninety seconds or four: the grey must be most of the
 * way in by the mouth shot whichever it was, and must never run ahead of the
 * story into a grey truck.
 */
const DRAIN_BAND: Readonly<Record<EndingStage, readonly [number, number]>> = {
  'the-hill': [0, 0.3],
  'the-grass': [0.25, 0.6],
  'the-find': [0.55, 0.65],
  'the-mouth': [0.62, 0.82],
  'the-turn': [0.8, 1],
  black: [1, 1],
};
/** Full drain, in seconds, at the in-band rate. */
const DRAIN_SECONDS = 40;
/** How fast it catches up when a stage raises the floor under it. */
const DRAIN_CATCH_UP = 0.25;

/** The bell's voices: the lift and the steps are muffled by her mouth; the last is not. */
const BELL_MUFFLED = 'holly-bell-muffled';
const BELL_LAST = 'holly-bell-last';

/** Where the held bell hangs from her snout pivot, in its local frame: under the disc. */
const HELD_AT = { x: 0, y: -0.035, z: 0.055 } as const;

const STAGE_FOR_CUE: Readonly<Record<string, EndingStage>> = {
  [ENDING_CUES.hill]: 'the-hill',
  [ENDING_CUES.grass]: 'the-grass',
  [ENDING_CUES.find]: 'the-find',
  [ENDING_CUES.mouth]: 'the-mouth',
  [ENDING_CUES.turn]: 'the-turn',
  [ENDING_CUES.black]: 'black',
};

/**
 * A soft red disc for the taillights: a lamp at 60 m is its glow and nothing
 * else. A `DataTexture` rather than a canvas so the room builds in the sim
 * lane, which has no DOM.
 */
function halo(core: readonly [number, number, number]): THREE.Texture {
  const size = 32;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const r = Math.hypot(x + 0.5 - size / 2, y + 0.5 - size / 2) / (size / 2);
      // A hot core and a quick falloff: the lamp, then its bloom in the haze.
      const a = Math.max(0, 1 - r) ** 2.2;
      const index = (y * size + x) * 4;
      const hot = Math.max(0, 1 - r * 3);
      data[index] = Math.round(core[0] + (255 - core[0]) * hot);
      data[index + 1] = Math.round(core[1] + (255 - core[1]) * hot * 0.5);
      data[index + 2] = Math.round(core[2] + (255 - core[2]) * hot * 0.3);
      data[index + 3] = Math.round(255 * a);
    }
  }
  const texture = new THREE.DataTexture(data, size, size);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

function assetOrThrow(id: string): THREE.Group {
  const asset = getAsset(id);
  if (asset === undefined) throw new Error(`hill: asset "${id}" is not registered`);
  return asset.create();
}

/**
 * The truck, driving away: beat 6's cab and trailer, forward along +Z in its
 * own frame and turned to run east (−X). Its taillights are the room's only
 * lamps, and they are glow sprites rather than fixtures: at the distances
 * this shot is about, a point light lights nothing she can see.
 */
function buildTruck(halo: THREE.Texture): { group: THREE.Group; lamps: THREE.SpriteMaterial } {
  const group = new THREE.Group();
  group.name = 'hill-truck';
  const cab = assetOrThrow('truck-cab');
  cab.position.z = 4.1;
  group.add(cab);
  const trailer = assetOrThrow('box-trailer');
  trailer.position.z = -3.3;
  group.add(trailer);
  const lamps = new THREE.SpriteMaterial({ map: halo, color: 0xffffff, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
  for (const side of [-1, 1]) {
    const lamp = new THREE.Sprite(lamps);
    lamp.name = `hill-taillight-${side > 0 ? 'left' : 'right'}`;
    lamp.position.set(side * 1.02, 1.05, -7.35);
    lamp.scale.setScalar(0.85);
    group.add(lamp);
  }
  // Forward (+Z) turned to −X: east, toward the sunrise.
  group.rotation.y = -Math.PI / 2;
  // Nothing on it moves but the whole: merged, or beat 6's cab and trailer are
  // ~80 draw calls a pass in a room with none to spare.
  collapseRigged(group, []);
  return { group, lamps };
}

export function createHillRoomModule(
  options: { part: number; dressing: boolean },
  capabilities: RoomCapabilities,
): RoomModule {
  const room = createHedgerow({ boards: 'broken', dressing: options.dressing, road: false, grassZ: { min: HILL_KNOLL_SOUTH, max: -32 } });
  // The room bakes beat 7's sky for its own branch; this module's environment
  // declares the same bake and the host owns that one.
  room.sky.dispose();
  capabilities.scene.add(room.group);
  const hill = createHillDressing({ dressing: options.dressing });
  capabilities.scene.add(hill);
  const colliders: ColliderHandle[] = [...hedgerowSolids({ road: false }), ...hillSolids()]
    .map((solid) => capabilities.colliders.add(solid));

  const tailHalo = halo([255, 40, 24]);
  const truck = buildTruck(tailHalo);
  capabilities.scene.add(truck.group);

  // ── The bell: one object, two parents (the plan: *"one parent transform"*) ──
  const lying = brassBellAsset.create();
  lying.position.set(HILL_BELL_AT.x, HILL_BELL_Y, HILL_BELL_AT.z);
  // Mouth downhill, strap trailing back up toward her: it rolled here.
  lying.rotation.y = 2.5;
  capabilities.scene.add(lying);
  const held = createHeldBell();
  held.visible = false;
  /**
   * The brass catching what light is left. The grade holds the bell's hue
   * (the accent ceiling), but a surface can only be as warm as its key, and
   * the drained key is grey — the first frames had a pale tan bell next to a
   * warmer pig. A glint is what brass does in a low sun that nothing else in
   * the field can: the plan's *"catching the light as the palette drains"*.
   * It rises with the drain, so it is nothing at the open and most at the end.
   */
  const glintHalo = halo([255, 176, 72]);
  const glintMaterial = new THREE.SpriteMaterial({ map: glintHalo, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0 });
  const glint = new THREE.Sprite(glintMaterial);
  glint.name = 'hill-bell-glint';
  glint.scale.setScalar(0.16);
  glint.position.set(0, 0.035, 0);
  lying.add(glint);
  let heldParent: THREE.Object3D | null = null;

  const staging = new EndingStaging();
  const bellWorld = new THREE.Vector3();
  const snoutWorld = new THREE.Vector3();
  /** Copied out of the borrowed tick; stage entries start from where she is. */
  const her = { x: HILL_SPAWN.x, z: HILL_SPAWN.z, facing: HILL_SPAWN.facing ?? 0 };
  const herWas = { x: her.x, z: her.z };
  let truckGoneRaised = false;
  let drain = 0;
  let blend: ZoneBlend | null = null;
  const strokes = { lift: 0, steps: 0, last: 0 };
  /** The truck's engine is voiced on the render step after its cue, like every sound here. */
  let truckVoiceOwed = false;
  let swing = 0;
  let swingVelocity = 0;
  let disposed = false;

  const outside = getZone(HEDGEROW_BLOOM_ZONE_ID);
  if (outside === undefined) throw new Error('hill: the outside zone is missing');
  const grey = endingZone();

  return {
    // Withheld until `the-grass`, the one stage its nudge counts in. Offered
    // from the first frame, a player who reached it while the truck was still
    // going got a prompt, pressed it, and nothing happened — then had to find
    // out it had to be pressed again (playthrough audit row 19). Not offered is
    // honest; offered and ignored is the thing D-209 says a verb must never be.
    interactables: [{ id: ENDING_IDS.bell, at: { x: HILL_BELL_AT.x, y: HILL_BELL_Y + 0.04, z: HILL_BELL_AT.z }, verbs: ['nudge'], reach: 0.55, enabled: false }],
    fixedStep(tick: RoomTick): void {
      // Her facing is not in the tick; her last step's heading stands in for it,
      // which is all the find's backstop needs (the way she was going).
      const dx = tick.player.x - herWas.x;
      const dz = tick.player.z - herWas.z;
      if (Math.hypot(dx, dz) > 0.002) her.facing = Math.atan2(dx, dz);
      herWas.x = tick.player.x;
      herWas.z = tick.player.z;
      her.x = tick.player.x;
      her.z = tick.player.z;
      staging.step(tick.dt);
      const state = staging.snapshot();
      if (!truckGoneRaised && state.truckGone) {
        truckGoneRaised = true;
        tick.raiseFlag(ENDING_FLAGS.truckGone);
      }
      if (state.stage === 'the-grass' && tick.nudged(ENDING_IDS.bell)) tick.raiseFlag(ENDING_FLAGS.bellFound);
    },
    get carry() {
      return staging.snapshot().carry;
    },
    handleCue(cue): void {
      const stage = STAGE_FOR_CUE[cue.id];
      if (stage === undefined) return;
      staging.enter(stage, her);
      if (stage === 'the-hill') truckVoiceOwed = true;
      if (stage === 'the-grass') capabilities.interaction.setEnabled(ENDING_IDS.bell, true);
      if (stage === 'the-mouth') capabilities.interaction.setEnabled(ENDING_IDS.bell, false);
      // The cut to black is the end of every other sound in the game (gate 5):
      // the wind stops ON the stroke, and the stroke is all that is left.
      if (stage === 'black') capabilities.cutAmbience();
    },
    renderStep(frameDt: number): void {
      if (disposed) return;
      const dt = Math.min(Math.max(0, frameDt), MAX_FRAME);
      const state = staging.snapshot();

      // ── The drain ─────────────────────────────────────────────────────────
      blend ??= capabilities.createZoneBlend(outside, grey, {
        fogScale: HEDGEROW_FOG_SCALE,
        exposureFrom: 1.52,
        exposureTo: 1.62,
        skyFrom: 1,
        skyTo: 0.62,
      });
      if (state.stage !== null) {
        const [floor, ceiling] = DRAIN_BAND[state.stage];
        const rate = drain < floor ? DRAIN_CATCH_UP : 1 / DRAIN_SECONDS;
        drain = Math.min(ceiling, drain + rate * dt);
      }
      blend.apply(drain);
      glintMaterial.opacity = 0.15 + 0.55 * drain;

      // ── The truck ─────────────────────────────────────────────────────────
      truck.group.visible = !state.truckGone;
      truck.group.position.set(state.truckX, HILL_ROAD.top, HILL_ROAD.laneZ);
      if (truckVoiceOwed) {
        truckVoiceOwed = false;
        capabilities.playOneShot('truck-away', { gain: 0.9, position: { x: state.truckX, y: HILL_ROAD.top + 1, z: HILL_ROAD.laneZ } });
      }

      // ── The bell ──────────────────────────────────────────────────────────
      if (state.bellHeld && heldParent === null) {
        heldParent = capabilities.her.snout;
        heldParent.add(held);
        held.position.set(HELD_AT.x, HELD_AT.y, HELD_AT.z);
        held.visible = true;
        lying.visible = false;
        held.add(glint);
        glint.position.set(0, -0.045, 0.02);
      }
      for (const event of staging.drainEvents()) {
        const at = { x: event.x, y: event.y, z: event.z };
        if (event.kind === 'lift') {
          strokes.lift += 1;
          capabilities.playOneShot(BELL_MUFFLED, { gain: 0.9, position: at });
          swingVelocity += 2.2;
        } else if (event.kind === 'step') {
          strokes.steps += 1;
          // A step moves the bell, and a bell that moved rings (the plan).
          capabilities.playOneShot(BELL_MUFFLED, { gain: 0.8, position: at, detune: (event.index % 2 === 0 ? -1 : 1) * 14 });
          swingVelocity += event.index % 2 === 0 ? 1.6 : -1.6;
        } else {
          strokes.last += 1;
          capabilities.playOneShot(BELL_LAST, { gain: 1, position: at });
        }
      }
      // A pendulum, lightly damped: the bell hangs from the loop in her teeth.
      swingVelocity += (-38 * swing - 3.2 * swingVelocity) * dt;
      swing += swingVelocity * dt;
      held.rotation.x = swing;
    },
    posePatch(pose: Pose): void {
      const dip = staging.snapshot().headDip;
      if (dip <= 0) return;
      // Down to the grass and up with it. Attention, not emotion (D-002): she
      // is looking at what she is picking up, and then at the ground ahead.
      pose.headPitch += 0.62 * dip;
      pose.snoutPitch += 0.28 * dip;
      pose.pitch += 0.1 * dip;
    },
    framing: () => null,
    diagnostics: (): RoomDiagnostics => {
      const state = staging.snapshot();
      (held.visible ? held : lying).getWorldPosition(bellWorld);
      const snout = heldParent === null ? null : heldParent.getWorldPosition(snoutWorld);
      return {
        kind: 'hill',
        hill: {
          stage: state.stage ?? 'none',
          truckX: state.truckX,
          truckGone: state.truckGone,
          bellHeld: state.bellHeld,
          bell: { x: bellWorld.x, y: bellWorld.y, z: bellWorld.z },
          snout: snout === null ? null : { x: snout.x, y: snout.y, z: snout.z },
          strokes: { ...strokes },
          drain,
          chromaCeiling: blend?.chromaCeiling ?? outside.chromaCeiling,
          headDip: state.headDip,
          wear: getWear(),
        },
      };
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const collider of colliders) capabilities.colliders.remove(collider);
      capabilities.scene.remove(room.group);
      capabilities.scene.remove(hill);
      capabilities.scene.remove(truck.group);
      capabilities.scene.remove(lying);
      held.removeFromParent();
      truck.lamps.dispose();
      tailHalo.dispose();
      glintHalo.dispose();
      glintMaterial.dispose();
    },
  };
}

/** Beat 7's dawn, where it ended: the `outside` zone at full bloom, before the drain takes it. */
function hillEnvironment(): RoomEnvironment {
  const zone = getZone(HEDGEROW_BLOOM_ZONE_ID);
  if (zone === undefined) throw new Error('hill: the outside zone is missing');
  return {
    zone,
    lighting: hedgerowLighting(),
    // Beat 7's bake with a gold middle band: the sky is the one place in the
    // beat where "dawn gold" can be literal, and the drain takes it with the
    // rest (the sky's intensity and the grade's ceiling both fall).
    background: { kind: 'sky', seed: 7, zenith: DAWN_ZENITH, middle: DAWN_GOLD },
    spawn: HILL_SPAWN,
    ambience: [{ id: 'field-air', at: { x: HILL_SPAWN.x, y: 2, z: HILL_SPAWN.z - 6 }, gain: 0.7 }],
  };
}

export const HILL_ROOM_MODULE: RoomModuleDefinition = {
  room: 'hill',
  // The hedgerow's far field: her notes go on beat 7's sheet.
  ledgerSheet: 'hedgerow',
  environment: hillEnvironment,
  create: createHillRoomModule,
};
